import { getDefaultInspectionCategories } from './adminDefaults';

export const INSPECTION_CATEGORY_FIELD_RE =
  /^inspection\.categories\.([^.]+)\.(status|score|total|items)$/;

export const isInspectionCategoryField = (name) =>
  Boolean(name && INSPECTION_CATEGORY_FIELD_RE.test(name));

export const expandInspectionCategoryFormValues = (row, values) => {
  const categories = row?.inspection?.categories ?? [];
  const catMap = new Map(
    (Array.isArray(categories) ? categories : []).map((cat) => [cat.key, cat])
  );

  getDefaultInspectionCategories().forEach(({ key, title }) => {
    const cat = catMap.get(key) || {
      key,
      title,
      score: 0,
      total: 0,
      status: 'pending',
      items: [],
    };

    values[`inspection.categories.${key}.status`] = cat.status || 'pending';
    values[`inspection.categories.${key}.score`] = cat.score ?? 0;
    values[`inspection.categories.${key}.total`] = cat.total ?? 0;
    values[`inspection.categories.${key}.items`] = Array.isArray(cat.items)
      ? cat.items.map((item) => item?.label || item).filter(Boolean).join('\n')
      : '';
  });
};

export const collapseInspectionCategoriesToPayload = (values) => {
  const categories = getDefaultInspectionCategories().map(({ key, title }) => {
    const itemsRaw = values[`inspection.categories.${key}.items`];
    const items = String(itemsRaw || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((label) => ({ label, status: 'pending' }));

    return {
      key,
      title,
      status: values[`inspection.categories.${key}.status`] || 'pending',
      score: Number(values[`inspection.categories.${key}.score`]) || 0,
      total: Number(values[`inspection.categories.${key}.total`]) || 0,
      items,
    };
  });

  const totals = categories.reduce(
    (acc, cat) => ({
      totalPoints: acc.totalPoints + (cat.total || 0),
      passedPoints: acc.passedPoints + (cat.score || 0),
    }),
    { totalPoints: 0, passedPoints: 0 }
  );

  const overallScore =
    totals.totalPoints > 0
      ? Math.round((totals.passedPoints / totals.totalPoints) * 100)
      : 0;

  return {
    categories,
    totalPoints: totals.totalPoints,
    passedPoints: totals.passedPoints,
    overallScore,
  };
};

export const applyInspectionCategoryPayload = (payload, values) => {
  const collapsed = collapseInspectionCategoriesToPayload(values);
  if (!payload.inspection) payload.inspection = {};
  payload.inspection.categories = collapsed.categories;
  payload.inspection.totalPoints = collapsed.totalPoints;
  payload.inspection.passedPoints = collapsed.passedPoints;
  if (collapsed.totalPoints > 0) {
    payload.inspection.overallScore = collapsed.overallScore;
  }
  return payload;
};

export const buildInspectionCategoryFields = () => {
  const fields = [];
  const statusOptions = ['pending', 'pass', 'warning', 'fail'];

  getDefaultInspectionCategories().forEach(({ key, title }) => {
    fields.push({
      name: `_section_cat_${key}`,
      label: title,
      type: 'section',
      categorySection: true,
    });
    fields.push({
      name: `inspection.categories.${key}.status`,
      label: 'Status',
      type: 'select',
      options: statusOptions,
      optionLabels: {
        pending: 'Pending',
        pass: 'Pass',
        warning: 'Needs attention',
        fail: 'Fail',
      },
      default: 'pending',
      categoryField: true,
    });
    fields.push({
      name: `inspection.categories.${key}.score`,
      label: 'Points Scored',
      type: 'number',
      default: 0,
      categoryField: true,
    });
    fields.push({
      name: `inspection.categories.${key}.total`,
      label: 'Max Points',
      type: 'number',
      default: 0,
      categoryField: true,
    });
    fields.push({
      name: `inspection.categories.${key}.items`,
      label: 'Checklist Items',
      type: 'lines',
      rows: 2,
      categoryField: true,
      hint: 'One check per line (optional)',
    });
  });

  return fields;
};
